import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Monitor, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FloatingPOSButton() {
  const [salesUrl, setSalesUrl] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupWindow, setPopupWindow] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    fetchSalesUrl();
    
    // Check popup status periodically
    const interval = setInterval(() => {
      if (popupWindow && popupWindow.closed) {
        setIsPopupOpen(false);
        setPopupWindow(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [popupWindow]);

  const fetchSalesUrl = async () => {
    try {
      const response = await axios.get(`${API}/settings/sales-url`);
      setSalesUrl(response.data.url || "https://merchantwebportal.axis.bank.in/wp_axis/Master/LOGIN3.aspx");
    } catch (error) {
      console.error("Error fetching sales URL:", error);
      setSalesUrl("https://merchantwebportal.axis.bank.in/wp_axis/Master/LOGIN3.aspx");
    }
  };

  const openPopup = useCallback(() => {
    const width = Math.min(1200, window.screen.width - 100);
    const height = Math.min(800, window.screen.height - 100);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      salesUrl,
      'POSSalesPortal',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=yes,scrollbars=yes,resizable=yes`
    );

    if (popup) {
      setPopupWindow(popup);
      setIsPopupOpen(true);
      popup.focus();
      setIsExpanded(false);
    } else {
      alert("Please allow popups for this site to open the POS portal.");
    }
  }, [salesUrl]);

  const focusPopup = () => {
    if (popupWindow && !popupWindow.closed) {
      popupWindow.focus();
      setIsExpanded(false);
    } else {
      openPopup();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 hidden md:block" data-testid="floating-pos-container">
      {/* Expanded Panel */}
      {isExpanded && (
        <div className="absolute bottom-16 right-0 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-bottom-2 duration-200">
          <div className="bg-gradient-to-r from-[#087DDF] to-[#0056b3] p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                <span className="font-semibold">POS Sales Portal</span>
              </div>
              <button 
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Quick access to your Axis Bank POS portal
            </p>
            {isPopupOpen ? (
              <Button
                className="w-full bg-green-500 hover:bg-green-600"
                onClick={focusPopup}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Show POS Window
              </Button>
            ) : (
              <Button
                className="w-full bg-[#087DDF] hover:bg-[#0056b3]"
                onClick={openPopup}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open POS Portal
              </Button>
            )}
            {isPopupOpen && (
              <p className="text-xs text-green-600 mt-2 text-center flex items-center justify-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                POS window is open
              </p>
            )}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          w-14 h-14 rounded-full shadow-lg flex items-center justify-center
          transition-all duration-200 hover:scale-110
          ${isPopupOpen 
            ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-green-500/30' 
            : 'bg-gradient-to-r from-[#087DDF] to-[#0056b3] shadow-[#087DDF]/30'
          }
        `}
        data-testid="floating-pos-btn"
      >
        <Monitor className="w-6 h-6 text-white" />
        {isPopupOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
        )}
      </button>
    </div>
  );
}
